// The searchable menu (DESIGN.md, "Searchable menus"): every filter, picker and long select
// shares these rows. One line per row (the full text on hover), so rows never overlap and long
// lists window at a fixed row height.

import { Check, Search, X } from "lucide-react";
import {
  forwardRef,
  type InputHTMLAttributes,
  type KeyboardEvent,
  type ReactNode,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import { cn } from "../lib/cn";
import { Button } from "./Button";
import { Popover } from "./Popover";
import { Skeleton } from "./Skeleton";

/** One choice in a menu. `label` is what search matches and what screen readers announce. */
export interface MenuOption {
  value: string;
  label: string;
  /** More text search matches (an email, a tag's category). */
  keywords?: string;
  /** The row's content when it is more than the label (a tag pill). Kept to one line. */
  content?: ReactNode;
  /** The full text on hover. Defaults to the label. */
  title?: string;
  /** Before the text: an avatar or an icon, 20px at most. */
  leading?: ReactNode;
  /** After the text, right aligned: a count. */
  hint?: ReactNode;
  disabled?: boolean;
}

/** Row height in px. Fixed, so a long list can render only the rows in view. */
export const MENU_ROW_H = 32;
/** Above this many rows the list renders only what is in view. */
export const MENU_WINDOW_AT = 60;
const LIST_PAD = 4;
const OVERSCAN = 8;

/** Options whose label or keywords contain the query, ignoring case. */
export function filterOptions(options: MenuOption[], query: string): MenuOption[] {
  const q = query.trim().toLowerCase();
  if (!q) return options;
  return options.filter(
    (o) => o.label.toLowerCase().includes(q) || (o.keywords?.toLowerCase().includes(q) ?? false),
  );
}

/** The options with the pinned ones first, each group in its own order. */
export function pinnedFirst(options: MenuOption[], pinned: ReadonlySet<string>): MenuOption[] {
  if (pinned.size === 0) return options;
  const first: MenuOption[] = [];
  const rest: MenuOption[] = [];
  for (const o of options) (pinned.has(o.value) ? first : rest).push(o);
  return [...first, ...rest];
}

/** Which rows to render for a scroll position: [start, end) with some overscan. */
export function windowRange(
  count: number,
  scrollTop: number,
  viewport: number,
): { start: number; end: number } {
  const top = Math.max(0, scrollTop - LIST_PAD);
  const inView = Math.ceil(viewport / MENU_ROW_H);
  const last = Math.max(0, count - inView - OVERSCAN);
  const start = Math.min(last, Math.max(0, Math.floor(top / MENU_ROW_H) - OVERSCAN));
  const end = Math.min(count, start + inView + OVERSCAN * 2 + 1);
  return { start, end };
}

export interface MenuSearchProps extends Omit<InputHTMLAttributes<HTMLInputElement>, "onChange"> {
  value: string;
  onValueChange: (value: string) => void;
  /** After the field, inside the row: a help mark. */
  trailing?: ReactNode;
}

/**
 * The search row at the top of a menu: an icon and a borderless field, a hairline under it. The
 * caret is the focus mark (the row is the menu's input, not a field inside it).
 */
export const MenuSearch = forwardRef<HTMLInputElement, MenuSearchProps>(function MenuSearch(
  { value, onValueChange, trailing, className, placeholder = "Search", ...props },
  ref,
) {
  return (
    <div
      className={cn(
        "flex h-9 shrink-0 items-center gap-2 border-hairline border-b pr-1.5 pl-3",
        className,
      )}
    >
      <Search className="size-4 shrink-0 text-ink-3" aria-hidden />
      <input
        ref={ref}
        type="text"
        autoComplete="off"
        spellCheck={false}
        placeholder={placeholder}
        aria-label={props["aria-label"] ?? placeholder}
        value={value}
        onChange={(e) => onValueChange(e.target.value)}
        className="h-full min-w-0 flex-1 bg-transparent text-body text-ink outline-none placeholder:text-ink-3"
        {...props}
      />
      {value ? (
        <button
          type="button"
          tabIndex={-1}
          aria-label="Clear search"
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onValueChange("")}
          className="inline-flex size-6 shrink-0 items-center justify-center rounded-pill text-ink-3 hover:bg-fill-2 hover:text-ink"
        >
          <X className="size-3.5" aria-hidden />
        </button>
      ) : null}
      {trailing}
    </div>
  );
});

/** The 16px box a multi-select row shows. Drawn, not a control: the row is the option. */
function CheckMark({ on }: { on: boolean }) {
  return (
    <span
      aria-hidden
      className={cn(
        "inline-flex size-4 shrink-0 items-center justify-center rounded-[5px]",
        on ? "bg-accent text-on-accent" : "shadow-[inset_0_0_0_1.5px_var(--vd-hairline-strong)]",
      )}
    >
      {on ? <Check className="size-3" strokeWidth={3} /> : null}
    </span>
  );
}

export interface OptionListProps {
  options: MenuOption[];
  /** The chosen values. */
  selected: readonly string[];
  /** Picks a value (single) or toggles it (multiple). */
  onSelect: (value: string) => void;
  multiple?: boolean;
  /** The list's accessible name ("Tags"). */
  label: string;
  /** The search row: always, never, or past this many options (default 8). */
  search?: boolean | number;
  /** Placeholder and name of the search field. */
  searchLabel?: string;
  /** The chosen ones first, in the order they were chosen when the menu opened (multiple). */
  selectedFirst?: boolean;
  /** Shown when the search matches nothing. */
  emptyText?: string;
  /** Shown when there are no options at all. */
  noOptionsText?: string;
  loading?: boolean;
  /** Clears every pick; shows Clear in the footer while something is picked. */
  onClear?: () => void;
  /** More of the footer, at the right (Match Any or All). */
  footer?: ReactNode;
  /** Rows shown before the list scrolls (default 10). */
  rows?: number;
  className?: string;
}

/**
 * A searchable list of options. Keys: arrows move, Enter picks, Space picks once the arrows have
 * moved (or with no search text), Home and End jump, typing filters. Esc belongs to the popover.
 */
export function OptionList({
  options,
  selected,
  onSelect,
  multiple = false,
  label,
  search = 8,
  searchLabel = "Search",
  selectedFirst = multiple,
  emptyText = "No matches",
  noOptionsText = "Nothing here yet",
  loading = false,
  onClear,
  footer,
  rows: visibleRows = 10,
  className,
}: OptionListProps) {
  const listId = useId();
  const [query, setQuery] = useState("");
  const [navigating, setNavigating] = useState(false);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const typeahead = useRef({ text: "", at: 0 });
  const [scrollTop, setScrollTop] = useState(0);
  // What was chosen when the menu opened goes first; picks made now stay where they are.
  const [pinned] = useState(() => new Set(selectedFirst ? selected : []));
  const chosen = useMemo(() => new Set(selected), [selected]);

  const showSearch = search === true || (typeof search === "number" && options.length > search);
  const shown = useMemo(
    () => filterOptions(pinnedFirst(options, pinned), query),
    [options, pinned, query],
  );
  const [active, setActive] = useState(() => {
    if (multiple) return 0;
    const i = shown.findIndex((o) => chosen.has(o.value));
    return i < 0 ? 0 : i;
  });

  // A new search starts at the top (not on open: a single choice opens on the chosen row).
  const typed = useRef(false);
  // biome-ignore lint/correctness/useExhaustiveDependencies: reset only when the text changes
  useEffect(() => {
    if (!typed.current) {
      typed.current = true;
      if (!multiple && active > 0) reveal(active);
      return;
    }
    setActive(0);
    if (listRef.current) listRef.current.scrollTop = 0;
    setScrollTop(0);
  }, [query]);

  const maxHeight = visibleRows * MENU_ROW_H + LIST_PAD * 2;
  const windowed = shown.length > MENU_WINDOW_AT;
  const viewport = listRef.current?.clientHeight || maxHeight;
  const { start, end } = windowed
    ? windowRange(shown.length, scrollTop, viewport)
    : { start: 0, end: shown.length };

  // Keeps the highlighted row in view when the keys move it.
  const reveal = (i: number) => {
    const el = listRef.current;
    if (!el) return;
    const top = LIST_PAD + i * MENU_ROW_H;
    const h = el.clientHeight || maxHeight;
    let next = el.scrollTop;
    if (top < next + LIST_PAD) next = top - LIST_PAD;
    else if (top + MENU_ROW_H > next + h - LIST_PAD) next = top + MENU_ROW_H - h + LIST_PAD;
    next = Math.max(0, next);
    if (next !== el.scrollTop) el.scrollTop = next;
    setScrollTop(next);
  };

  const move = (i: number) => {
    if (shown.length === 0) return;
    const next = Math.max(0, Math.min(shown.length - 1, i));
    setActive(next);
    setNavigating(true);
    reveal(next);
  };

  const pick = (i: number) => {
    const o = shown[i];
    if (o && !o.disabled) onSelect(o.value);
  };

  const onKeyDown = (e: KeyboardEvent<HTMLElement>) => {
    const inField = e.target === inputRef.current;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      move(active + 1);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      move(active - 1);
    } else if ((e.key === "Home" || e.key === "End") && (!inField || navigating || !query)) {
      e.preventDefault();
      move(e.key === "Home" ? 0 : shown.length - 1);
    } else if (e.key === "PageDown" || e.key === "PageUp") {
      e.preventDefault();
      move(active + (e.key === "PageDown" ? visibleRows : -visibleRows));
    } else if (e.key === "Enter") {
      if (shown[active]) {
        e.preventDefault();
        pick(active);
      }
    } else if (e.key === " " && (!inField || navigating || query === "")) {
      if (shown[active]) {
        e.preventDefault();
        pick(active);
      }
    } else if (!inField && e.key.length === 1 && !e.ctrlKey && !e.metaKey && !e.altKey) {
      // No search row: typing jumps to the first row that starts with what was typed.
      const now = Date.now();
      const t = typeahead.current;
      t.text = now - t.at > 700 ? e.key : t.text + e.key;
      t.at = now;
      const low = t.text.toLowerCase();
      const i = shown.findIndex((o) => o.label.toLowerCase().startsWith(low));
      if (i >= 0) move(i);
    }
  };

  const activeId = shown[active] ? `${listId}-${active}` : undefined;
  const nothing = !loading && shown.length === 0;
  const showFooter = Boolean(footer) || (onClear && selected.length > 0);

  return (
    // biome-ignore lint/a11y/noStaticElementInteractions: keys from the field and the list land here
    <div className={cn("flex min-w-0 flex-col", className)} onKeyDown={onKeyDown}>
      {showSearch ? (
        <MenuSearch
          ref={inputRef}
          autoFocus
          role="combobox"
          aria-label={searchLabel}
          placeholder={searchLabel}
          aria-expanded
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={activeId}
          value={query}
          onValueChange={(v) => {
            setQuery(v);
            setNavigating(false);
          }}
        />
      ) : null}
      <div
        ref={listRef}
        id={listId}
        role="listbox"
        aria-label={label}
        aria-multiselectable={multiple || undefined}
        aria-activedescendant={showSearch ? undefined : activeId}
        tabIndex={showSearch ? -1 : 0}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        style={{ maxHeight }}
        className="overflow-y-auto overscroll-contain px-1 outline-none"
      >
        <div style={{ height: LIST_PAD + start * MENU_ROW_H }} aria-hidden />
        {shown.slice(start, end).map((o, k) => {
          const i = start + k;
          const on = chosen.has(o.value);
          return (
            <div
              key={o.value}
              id={`${listId}-${i}`}
              role="option"
              tabIndex={-1}
              aria-label={o.title ?? o.label}
              aria-selected={on}
              aria-disabled={o.disabled || undefined}
              data-active={i === active || undefined}
              title={o.title ?? o.label}
              onMouseMove={() => {
                if (i !== active) setActive(i);
              }}
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => {
                setActive(i);
                pick(i);
              }}
              onKeyDown={() => undefined}
              className={cn(
                "flex cursor-default select-none items-center gap-2.5 overflow-hidden whitespace-nowrap rounded-sm px-2 text-body text-ink",
                i === active && "bg-fill-2",
                o.disabled && "opacity-45",
              )}
              style={{ height: MENU_ROW_H }}
            >
              {multiple ? <CheckMark on={on} /> : null}
              {o.leading ? (
                <span className="flex shrink-0 items-center text-ink-3 [&_svg]:size-4">
                  {o.leading}
                </span>
              ) : null}
              <span className="flex min-w-0 flex-1 items-center gap-2">
                {o.content ?? <span className="min-w-0 truncate">{o.label}</span>}
              </span>
              {o.hint !== undefined && o.hint !== null ? (
                <span className="shrink-0 text-caption text-ink-3 tnum">{o.hint}</span>
              ) : null}
              {!multiple ? (
                <Check
                  aria-hidden
                  className={cn("size-4 shrink-0 text-accent-ink", on ? "" : "invisible")}
                />
              ) : null}
            </div>
          );
        })}
        <div style={{ height: LIST_PAD + (shown.length - end) * MENU_ROW_H }} aria-hidden />
        {loading && shown.length === 0 ? (
          <div aria-hidden className="flex flex-col gap-2 px-2 pb-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-1/2" />
          </div>
        ) : null}
        {nothing ? (
          <p role="status" className="px-2 pt-2 pb-4 text-center text-caption text-ink-3">
            {options.length === 0 ? noOptionsText : emptyText}
          </p>
        ) : null}
      </div>
      {showFooter ? (
        <div className="flex h-10 shrink-0 items-center gap-2 border-hairline border-t px-1.5">
          {onClear && selected.length > 0 ? (
            <Button size="sm" variant="ghost" onClick={onClear}>
              Clear
            </Button>
          ) : null}
          <span className="flex-1" />
          {footer}
        </div>
      ) : null}
    </div>
  );
}

export interface FilterMenuProps extends OptionListProps {
  /** The chip or field that opens the menu. */
  trigger: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  align?: "start" | "center" | "end";
  /** Panel width in px (default 280). The trigger's width when it is wider. */
  width?: number;
  /** Close after a pick (default: single choice only). */
  closeOnSelect?: boolean;
}

/** A trigger that opens an `OptionList` in an opaque popover. */
export function FilterMenu({
  trigger,
  open: openProp,
  onOpenChange,
  align = "start",
  width = 280,
  closeOnSelect,
  onSelect,
  multiple = false,
  ...list
}: FilterMenuProps) {
  const [own, setOwn] = useState(false);
  const open = openProp ?? own;
  const setOpen = (o: boolean) => {
    if (openProp === undefined) setOwn(o);
    onOpenChange?.(o);
  };
  const closes = closeOnSelect ?? !multiple;
  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align={align}
      label={list.label}
      className="flex max-w-[calc(100vw-16px)] flex-col overflow-hidden p-0"
      trigger={trigger}
    >
      <div
        style={{ width: `max(${width}px, var(--radix-popover-trigger-width))` }}
        className="flex max-w-full flex-col"
      >
        <OptionList
          {...list}
          multiple={multiple}
          onSelect={(v) => {
            onSelect(v);
            if (closes) setOpen(false);
          }}
        />
      </div>
    </Popover>
  );
}
